import { useState } from 'react';
import { AlertTriangle, Check, Film, Loader2, Play, X } from 'lucide-react';
import { Async, EmptyState, PageHeader } from '../../components/ui/States';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useUser } from '../../auth/AuthContext';
import { canReview } from '../../auth/roles';
import { api } from '../../lib/api';
import { errorMessage } from '../../hooks/useApi';
import { useApi } from '../../hooks/useApi';
import { btnDanger, btnGhost, cardCls, formatDate, timeAgo } from './shared';

interface QueueItem {
  id: string;
  title: string;
  slug: string;
  genres: string[];
  synopsis: string | null;
  posterVerticalUrl: string | null;
  submittedAt: string;
  creatorName: string | null;
  episodeCount: number;
  transcodedEpisodeCount: number;
}

type PreviewStatus = 'ready' | 'processing' | 'failed' | 'no_video' | 'no_episodes' | 'misconfigured';
interface PreviewState {
  title: string;
  loading: boolean;
  status?: PreviewStatus;
  url?: string | null;
  error?: string;
}

const PREVIEW_COPY: Record<Exclude<PreviewStatus, 'ready'>, string> = {
  processing: 'The video is still being prepared. Try again in a minute.',
  failed: 'Preparing the video failed. The creator needs to upload it again.',
  no_video: 'No video has been uploaded for this title yet.',
  no_episodes: 'This title has no episodes yet.',
  misconfigured: 'Video playback is not set up on the server yet, so no preview can be shown.',
};

export function ReviewQueue() {
  const user = useUser();
  const canDecide = canReview(user.role);
  const state = useApi<{ queue: QueueItem[] }>('/admin/queue');

  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [rejecting, setRejecting] = useState<QueueItem | null>(null);
  const [notes, setNotes] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const openPreview = async (item: QueueItem) => {
    setPreview({ title: item.title, loading: true });
    try {
      const r = await api<{ status: PreviewStatus; url: string | null }>(`/admin/titles/${item.id}/preview`);
      setPreview({ title: item.title, loading: false, status: r.status, url: r.url });
    } catch (err) {
      setPreview({ title: item.title, loading: false, error: errorMessage(err) });
    }
  };

  const approve = async (item: QueueItem) => {
    setBusyId(item.id);
    setActionError(null);
    try {
      await api(`/admin/queue/${item.id}/approve`, { method: 'POST' });
      state.reload();
    } catch (err) {
      setActionError(`Could not approve "${item.title}": ${errorMessage(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (!rejecting) return;
    setBusyId(rejecting.id);
    setActionError(null);
    try {
      const trimmed = notes.trim();
      await api(`/admin/queue/${rejecting.id}/reject`, { method: 'POST', body: trimmed ? { notes: trimmed } : {} });
      setRejecting(null);
      setNotes('');
      state.reload();
    } catch (err) {
      setActionError(`Could not reject "${rejecting.title}": ${errorMessage(err)}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <PageHeader
        title="Review queue"
        subtitle={
          canDecide
            ? 'Titles submitted by creators. Watch a sample, then approve to publish or reject with a note.'
            : 'Titles submitted by creators. You can look at them, but only reviewers can approve or reject.'
        }
      />
      {actionError && (
        <p className="mb-4 px-4 py-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-xs text-rose-300" role="alert">
          {actionError}
        </p>
      )}
      <Async state={state}>
        {({ queue }) =>
          queue.length === 0 ? (
            <EmptyState title="Nothing is waiting for review" message="When a creator submits a title, it will appear here." />
          ) : (
            <ul className="space-y-4">
              {queue.map((item) => (
                <li key={item.id} className={`${cardCls} p-5 flex flex-col md:flex-row gap-5`}>
                  <div className="w-20 h-28 rounded-lg overflow-hidden shrink-0 border border-[#2c2140] bg-[#171022] flex items-center justify-center">
                    {item.posterVerticalUrl ? (
                      <img src={item.posterVerticalUrl} alt={`Poster for ${item.title}`} className="w-full h-full object-cover" />
                    ) : (
                      <Film className="w-6 h-6 text-gray-600" aria-label="No poster" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-bold text-white">{item.title}</h3>
                      <Badge variant="warning">Waiting for review</Badge>
                    </div>
                    <p className="text-xs text-gray-400">
                      Submitted by {item.creatorName ?? 'Unknown creator'} · <span title={formatDate(item.submittedAt)}>{timeAgo(item.submittedAt)}</span>
                    </p>
                    {item.genres.length > 0 && <p className="text-xs text-violet-300">{item.genres.join(' · ')}</p>}
                    <p className="text-xs text-gray-300 line-clamp-3">{item.synopsis || 'No synopsis was provided.'}</p>
                    <p className="text-xs text-gray-500">
                      {item.episodeCount} {item.episodeCount === 1 ? 'episode' : 'episodes'}, {item.transcodedEpisodeCount} with video ready
                    </p>
                  </div>
                  <div className="flex md:flex-col items-stretch gap-2 md:w-40 shrink-0">
                    <button onClick={() => openPreview(item)} className={`${btnGhost} flex items-center justify-center gap-1.5`}>
                      <Play className="w-3.5 h-3.5" /> Watch sample
                    </button>
                    {canDecide && (
                      <>
                        <button
                          onClick={() => approve(item)}
                          disabled={busyId !== null}
                          className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5"
                        >
                          {busyId === item.id && !rejecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          Approve and publish
                        </button>
                        <button
                          onClick={() => {
                            setActionError(null);
                            setNotes('');
                            setRejecting(item);
                          }}
                          disabled={busyId !== null}
                          className="px-3 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1.5"
                        >
                          <X className="w-3.5 h-3.5" /> Reject
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )
        }
      </Async>

      <Modal open={preview !== null} onOpenChange={(o) => !o && setPreview(null)} title={`Sample: ${preview?.title ?? ''}`} maxWidth="2xl">
        <div className="aspect-video rounded-xl overflow-hidden bg-black border border-[#241b33] flex items-center justify-center">
          {preview?.loading ? (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin text-violet-400" /> Getting the video…
            </div>
          ) : preview?.status === 'ready' && preview.url ? (
            <video src={preview.url} controls autoPlay className="w-full h-full object-contain" />
          ) : (
            <div className="flex flex-col items-center gap-2 px-8 text-center text-xs text-gray-300">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
              <span>{preview?.error ?? (preview?.status && preview.status !== 'ready' ? PREVIEW_COPY[preview.status] : 'No preview is available.')}</span>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        open={rejecting !== null}
        onOpenChange={(o) => !o && busyId === null && setRejecting(null)}
        title={`Reject "${rejecting?.title ?? ''}"?`}
        description="The title will not be published. Tell the creator why so they can fix it."
        maxWidth="md"
      >
        <div className="space-y-4">
          <div>
            <label htmlFor="reject-notes" className="block text-xs font-semibold text-gray-300 mb-1">
              Note for the creator (optional)
            </label>
            <textarea
              id="reject-notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="For example: episode 3 has no sound, please upload it again."
              className="w-full p-2.5 bg-[#171022] border border-[#2c2140] rounded-lg text-xs text-white focus:outline-none focus:border-rose-500 resize-none"
            />
          </div>
          {actionError && <p className="text-xs text-rose-400">{actionError}</p>}
          <div className="flex justify-end gap-2">
            <button onClick={() => setRejecting(null)} disabled={busyId !== null} className={btnGhost}>
              Cancel
            </button>
            <button onClick={reject} disabled={busyId !== null} className={btnDanger}>
              {busyId !== null ? 'Rejecting…' : 'Reject title'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
